import { useEffect, useState } from "react";
import { catalog } from "@/lib/api";
import { paketHematList, type PaketHemat } from "@/lib/paket-hemat";

// One catalogue request per page load, shared by the header link and the Paket
// Hemat page, the way use-promo shares the promotion. Only ever read in an
// effect: at build time there is no API, so the prerendered header has no link
// and the first paint agrees with it.
let pending: Promise<PaketHemat[] | null> | null = null;

function load(fresh: boolean): Promise<PaketHemat[] | null> {
  if (fresh || !pending) {
    pending = catalog
      .list()
      .then((r) => paketHematList(r.products ?? []))
      // An outage reads as "not known", not "none": the header hides the
      // link either way, but the page says it could not load.
      .catch(() => null);
  }
  return pending;
}

export type PaketHematState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; list: PaketHemat[] };

/**
 * The live Paket Hemat. `fresh` refetches instead of reusing this page load's
 * answer, for the page itself, where the stock shown should be current.
 */
export function usePaketHemat({ fresh = false } = {}): PaketHematState {
  const [state, setState] = useState<PaketHematState>({ status: "loading" });
  useEffect(() => {
    let live = true;
    void load(fresh).then((list) => {
      if (live) setState(list ? { status: "ready", list } : { status: "error" });
    });
    return () => {
      live = false;
    };
  }, [fresh]);
  return state;
}
